"use client"

import React, { useState, useEffect } from "react"
import Link from "next/link"
import { useTheme } from "next-themes"
import { usePathname } from "next/navigation"
import { Button } from "../ui/button"
import { Download, Plug, PlugZap } from "lucide-react"
import { ThemeToggle } from "./theme-toggle"
import { NotificationBell } from "./notification-bell"
import { WorldClock } from "./world-clock"
import { LiveNews } from "./live-news"
import { StatusPulse } from "./status-pulse"
import { cn } from "../lib/utils"
import { toast } from "sonner"
import {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "../ui/breadcrumb"
import { SidebarTrigger, useSidebar } from "../ui/sidebar"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip"

export interface BreadcrumbConfig {
    label: string
    href?: string
}

interface HeaderProps {
    /** Breadcrumb configuration - if not provided, will auto-generate from pathname */
    breadcrumbs?: BreadcrumbConfig[]
    /** Custom breadcrumb generator function */
    generateBreadcrumbs?: (pathname: string) => BreadcrumbConfig[]
    /** Session ID for download functionality */
    sessionId?: string | null
    /** WebSocket connection status */
    isWebSocketConnected?: boolean
    /** Distance from logo when sidebar is expanded */
    sidebarToggleDistance?: string
    /** Document name for breadcrumb display */
    documentName?: string
    /** Show download button */
    showDownload?: boolean
    /** Show WebSocket status indicator */
    showWebSocketStatus?: boolean
    /** Show Spotify player */
    showSpotifyPlayer?: boolean
    /** Custom actions to render in header */
    actions?: React.ReactNode
    /** Logo URL override (light theme) */
    logoLight?: string
    /** Logo URL override (dark theme) */
    logoDark?: string
    /** Logo symbol URL override (light theme, for collapsed sidebar) */
    logoSymbolLight?: string
    /** Logo symbol URL override (dark theme, for collapsed sidebar) */
    logoSymbolDark?: string
    /** Logo link destination */
    logoHref?: string
    /** Show notifications bell (default true); bell hides itself when getAuthToken not provided or returns null */
    showNotifications?: boolean
    /** API base URL for notifications (default from env); use "" for same-origin */
    notificationsApiBaseUrl?: string
    /** Link for "View all" in notification dropdown (e.g. "/notifications") */
    notificationsHref?: string
    /** Returns current auth token for notification API; when provided, bell is shown and used for requests */
    getAuthToken?: () => string | null
    /** Show world clock (default true) */
    showWorldClock?: boolean
    /** Show live news component (default true) */
    showLiveNews?: boolean
    /** Show the Status Pulse (device→backend connectivity) control (default true). */
    showStatusPulse?: boolean
    /** Show theme toggle (default true). Set false on apps that are
     * intentionally locked to a single theme (e.g. access.askfinz.ai is
     * dark-only operator console — the button would be a no-op there). */
    showThemeToggle?: boolean
}

export function Header({
    breadcrumbs: breadcrumbsProp,
    generateBreadcrumbs: generateBreadcrumbsProp,
    sessionId,
    isWebSocketConnected = false,
    sidebarToggleDistance = "7rem",
    documentName,
    showDownload = false,
    showWebSocketStatus = false,
    showSpotifyPlayer = false,
    actions,
    logoLight,
    logoDark,
    logoSymbolLight,
    logoSymbolDark,
    logoHref = "/",
    showNotifications = true,
    notificationsApiBaseUrl,
    notificationsHref,
    getAuthToken,
    showWorldClock = true,
    showLiveNews = true,
    showStatusPulse = true,
    showThemeToggle = true,
}: HeaderProps) {
    const [mounted, setMounted] = useState(false)
    const { theme, systemTheme } = useTheme()
    const currentTheme = theme === "system" ? systemTheme : theme
    const pathname = usePathname()

    const sidebar = useSidebar()
    const dynamicDistance = sidebar.state === "collapsed"
        ? "10.5rem"
        : "15.125rem"

    const defaultGenerateBreadcrumbs = (pathname: string): BreadcrumbConfig[] => {
        const pathSegments = pathname.split('/').filter(Boolean)
        const breadcrumbs: BreadcrumbConfig[] = [{ label: 'Home', href: logoHref }]

        const labelMap: Record<string, string> = {
            'dashboard': 'Dashboard',
            'chat': 'Chat',
            'history': 'History',
            'search': 'Search',
            'products': 'Products',
            'patents': 'Patents',
            'sec': 'SEC Filings',
            'rdp': 'RDP',
            'manage': 'Manage',
            'documents': 'Documents',
            'templates': 'Templates',
            'foundry': 'Foundry',
            'analytics': 'Analytics',
            'revenue': 'Revenue',
            'usage': 'Usage',
            'api-keys': 'Usage',
            'llm': 'LLM',
            'logs': 'Logs',
            'estate': 'Estate',
            'code': 'Code',
            'docs': 'Documentation',
        }

        let currentPath = ''
        pathSegments.forEach((segment, index) => {
            currentPath += `/${segment}`
            const isLast = index === pathSegments.length - 1

            if (isLast && documentName) {
                breadcrumbs.push({
                    label: documentName,
                    href: undefined,
                })
            } else {
                const label = labelMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1)
                breadcrumbs.push({
                    label,
                    href: isLast ? undefined : currentPath,
                })
            }
        })

        return breadcrumbs
    }

    const breadcrumbs = breadcrumbsProp ||
        (generateBreadcrumbsProp ? generateBreadcrumbsProp(pathname) : defaultGenerateBreadcrumbs(pathname))

    useEffect(() => {
        setMounted(true)
    }, [])

    const handleDownload = async () => {
        if (!sessionId) {
            toast.info("No conversation to download yet.")
            return
        }

        try {
            const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:9001"
            const response = await fetch(`${API_BASE_URL}/api/conversation/${sessionId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data = await response.json()

            const blob = new Blob([JSON.stringify(data, null, 2)], {
                type: "application/json",
            })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = `conversation_${sessionId}.json`
            document.body.appendChild(a)
            a.click()
            setTimeout(() => {
                window.URL.revokeObjectURL(url)
                document.body.removeChild(a)
            }, 100)
        } catch (error) {
            console.error("Error downloading conversation:", error)
            toast.error(`Error downloading conversation: ${error instanceof Error ? error.message : "Unknown error"}`)
        }
    }

    const getLogoSrc = () => {
        if (sidebar.state === "collapsed") {
            return currentTheme === "dark"
                ? (logoSymbolDark || "/shared/logos/header/AskFinz_symbol_light.svg")
                : (logoSymbolLight || "/shared/logos/header/AskFinz_symbol_black.svg")
        } else {
            return currentTheme === "dark"
                ? (logoDark || "/shared/logos/header/askFinz_white.svg")
                : (logoLight || "/shared/logos/header/askFinz_black.svg")
        }
    }

    if (!mounted) {
        return (
            <div data-header className="sticky top-0 z-[100] flex h-16 items-center justify-between border-b backdrop-blur supports-[backdrop-filter]:backdrop-blur pl-0 pr-3 sm:pr-4 relative" style={{ backgroundColor: 'hsl(var(--header-footer-background))' }}>
                <div className="flex items-center gap-2 pl-3 sm:pl-4">
                    <Link href={logoHref} className="inline-flex items-center">
                        <img
                            src="/shared/logos/header/askFinz_black.svg"
                            alt="AskFinz"
                            className="h-5 sm:h-6 w-auto object-contain max-w-[70px]"
                        />
                    </Link>
                    <div style={{ left: dynamicDistance, position: 'absolute' }} className="flex items-center gap-2">
                        <SidebarTrigger />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div data-header className="flex h-16 items-center justify-between border-b backdrop-blur supports-[backdrop-filter]:backdrop-blur pl-0 pr-3 sm:pr-4 w-full relative z-[100]" style={{ backgroundColor: 'hsl(var(--header-footer-background))' }}>
            <div className="flex items-center min-w-0 gap-2 pl-3 sm:pl-4">
                <Link href={logoHref} className="inline-flex items-center">
                    <img
                        src={getLogoSrc()}
                        alt="AskFinz"
                        className="h-5 sm:h-6 w-auto object-contain max-w-[70px]"
                        onError={(e) => {
                            const target = e.currentTarget as HTMLImageElement
                            const fallback = currentTheme === "dark" ? "/shared/logos/header/askFinz_white.svg" : "/shared/logos/header/askFinz_black.svg"
                            if (target.src !== fallback) target.src = fallback
                        }}
                    />
                </Link>
                <div style={{ left: dynamicDistance, position: 'absolute' }} className="flex items-center gap-2">
                    <SidebarTrigger />
                    <div className="flex items-center">
                        <Breadcrumb>
                            <BreadcrumbList>
                                {breadcrumbs.map((crumb, index) => (
                                    <React.Fragment key={index}>
                                        <BreadcrumbItem>
                                            {crumb.href ? (
                                                <BreadcrumbLink asChild>
                                                    <Link href={crumb.href}>{crumb.label}</Link>
                                                </BreadcrumbLink>
                                            ) : (
                                                <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                                            )}
                                        </BreadcrumbItem>
                                        {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                                    </React.Fragment>
                                ))}
                            </BreadcrumbList>
                        </Breadcrumb>
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0 min-w-0">
                {showSpotifyPlayer && (
                    <div className="mr-2">
                        {/* SpotifyPlayer component would be imported from the specific app */}
                    </div>
                )}

                {showWebSocketStatus && (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-9 w-9 p-0"
                                    onClick={() => {
                                        const message = isWebSocketConnected
                                            ? "WebSocket is connected and ready."
                                            : "WebSocket is disconnected. Some features may not be available."

                                        if (isWebSocketConnected) {
                                            toast.success("WebSocket", { description: message })
                                        } else {
                                            toast.warning("WebSocket", { description: message })
                                        }
                                    }}
                                >
                                    {isWebSocketConnected ? (
                                        <PlugZap className="h-4 w-4 text-green-500" />
                                    ) : (
                                        <Plug className="h-4 w-4 text-red-500" />
                                    )}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                                {isWebSocketConnected ? "WebSocket Connected" : "WebSocket Disconnected"}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}

                {showDownload && (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleDownload}
                                    disabled={!sessionId}
                                    className="h-9 w-9 p-0"
                                >
                                    <Download className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                                Download conversation
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}

                {actions}

                {/* World clock + live news are space-hungry; hide on phones so
                    the essential actions (notifications, status, theme) always fit. */}
                {showWorldClock && <span className="hidden md:contents"><WorldClock getAuthToken={getAuthToken} /></span>}

                {showLiveNews && <span className="hidden md:contents"><LiveNews /></span>}

                {/* SECURITY V-029: the bell self-gates on the session-marker
                    cookie (isAuthenticated), so it no longer requires a
                    JS-readable token to render. getAuthToken is passed through
                    only for the legacy Bearer header. */}
                {showNotifications && (
                    <NotificationBell
                        getAuthToken={getAuthToken}
                        apiBaseUrl={notificationsApiBaseUrl}
                        notificationsHref={notificationsHref}
                    />
                )}

                {showStatusPulse && <StatusPulse />}

                {showThemeToggle && <ThemeToggle />}
            </div>
        </div>
    )
}

